import React, { useEffect, useState } from "react";
import { usePolling } from "../lib/usePolling";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { TeamBadge } from "../components/TeamBadge";
import { useFollows } from "../context/FollowsContext";
import { useTranslation } from "react-i18next";
import { CaretLeft, Users, Clock, Sparkle, Bell, BellRinging } from "@phosphor-icons/react";
import { kickoffTimeTz, kickoffDate } from "../lib/datetime";
import { LanguageSelector } from "../components/LanguageSelector";

const StatusPill = ({ m }) => {
  const { t } = useTranslation();
  if (m.is_live)
    return (
      <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-perf-danger/15 border border-perf-danger/40 text-perf-danger text-xs font-bold" data-testid="live-status">
        <span className="w-2 h-2 rounded-full bg-perf-danger animate-pulse" />
        {m.status?.long || t("livematch.live")} {m.status?.elapsed ? `· ${m.status.elapsed}'` : ""}
      </span>
    );
  if (m.is_finished)
    return <span className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-xs font-bold">{t("livematch.finished")}</span>;
  return (
    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/15 border border-gold/40 text-gold text-xs font-bold">
      <Clock size={13} weight="bold" /> {kickoffTimeTz(m)} · {kickoffDate(m)}
    </span>
  );
};

function Pitch({ side, data, accent }) {
  const { t } = useTranslation();
  if (!data) return null;
  const rows = {};
  (data.startXI || []).forEach((p) => {
    const r = p.grid ? p.grid.split(":")[0] : "0";
    (rows[r] = rows[r] || []).push(p);
  });
  const keys = Object.keys(rows).sort((a, b) => Number(a) - Number(b));
  const useGrid = keys.length > 1;

  return (
    <div className="hm-card p-4" data-testid={`lineup-${side}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-bold">{data.team}</span>
        {data.formation && (
          <span className="px-2 py-0.5 rounded-md bg-white/5 text-[11px] font-numeral font-bold" style={{ color: accent }}>
            {data.formation}
          </span>
        )}
      </div>

      {useGrid ? (
        <div
          className="rounded-xl p-3 space-y-3"
          style={{ background: "linear-gradient(180deg, rgba(0,230,118,0.05), rgba(212,175,55,0.04))", border: "1px solid rgba(255,255,255,0.06)" }}
        >
          {keys.map((k) => (
            <div key={k} className="flex justify-around gap-2">
              {rows[k].map((p, i) => (
                <div key={i} className="flex flex-col items-center gap-1 max-w-[70px]">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center font-numeral font-bold text-xs text-black" style={{ background: accent }}>
                    {p.number ?? "?"}
                  </span>
                  <span className="text-[10px] text-white/70 text-center leading-tight truncate w-full">{(p.name || "").split(" ").slice(-1)[0]}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-1.5">
          {(data.startXI || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="w-6 font-numeral font-bold text-right" style={{ color: accent }}>{p.number ?? "-"}</span>
              <span className="text-white/80">{p.name}</span>
              <span className="ml-auto text-[10px] text-white/40">{p.pos}</span>
            </div>
          ))}
        </div>
      )}

      {data.subs?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1.5">{t("livematch.subs")}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {data.subs.map((p, i) => (
              <span key={i} className="text-[11px] text-white/60">{p.number ?? ""} {(p.name || "").split(" ").slice(-1)[0]}</span>
            ))}
          </div>
        </div>
      )}
      {data.coach && <p className="mt-2 text-[11px] text-white/40">{t("livematch.coach")} : <span className="text-white/70">{data.coach}</span></p>}
    </div>
  );
}

function Bar({ label, pct, color, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <span className="w-8 text-[11px] font-bold text-white/60">{label}</span>
      <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-10 text-right font-numeral font-bold text-sm">{pct}%</span>
    </div>
  );
}

function LivePreds({ fixtureId }) {
  const { t, i18n } = useTranslation();
  const [p, setP] = useState(null);
  // temps réel : poll 20s (recalcul aussi sur carton)
  usePolling((signal) => api.get(`/live/predictions/${fixtureId}`, { signal })
    .then((r) => { if (!signal.aborted) setP(r.data); }).catch(() => {}), 20000, [fixtureId, i18n.language]);

  if (!p || !p.available) return null;
  const pr = p.probabilities;
  const pc = (x) => Math.round((x || 0) * 100);
  return (
    <div className="hm-card p-4 mt-6" data-testid="live-prediction"
      style={{ background: p.card_adjusted ? "linear-gradient(135deg, rgba(229,57,53,0.12), rgba(18,18,18,1))" : undefined,
               borderColor: p.card_adjusted ? "rgba(229,57,53,0.4)" : undefined }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <Sparkle size={16} weight="fill" color="#D4AF37" />
          <span className="text-sm font-bold">{t("livematch.live_pred")}</span>
        </div>
        {p.card_adjusted && (
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-perf-danger/20 border border-perf-danger/40 text-perf-danger text-[10px] font-black" data-testid="card-adjusted-badge">
            🟥 {t("livematch.card_adjusted")}
          </span>
        )}
      </div>
      <div className="space-y-2">
        <Bar label="1" pct={pc(pr.home)} color="#00E676" testid="prob-home" />
        <Bar label="N" pct={pc(pr.draw)} color="#8A8A8A" testid="prob-draw" />
        <Bar label="2" pct={pc(pr.away)} color="#D4AF37" testid="prob-away" />
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 text-[11px]">
        <span className="text-white/55">{t("livematch.likely_score")} : <b className="text-white">{pr.likely_score}</b>{pr.likely_score_prob != null ? ` (${pr.likely_score_prob}%)` : ""}</span>
        <span className="text-white/55">{t("livematch.over25")} : <b className="text-white">{pc(pr.over25)}%</b></span>
      </div>
      {p.last_trigger && (
        <p className="text-[10px] text-perf-danger mt-2" data-testid="live-trigger">
          {t("livematch.recalc_at", { min: p.last_trigger.minute })} ({p.last_trigger.type === "second_yellow" ? t("livematch.second_yellow") : t("livematch.red_card")} · {p.last_trigger.side === "home" ? t("livematch.home_side") : t("livematch.away_side")})
        </p>
      )}
      <p className="text-[10px] text-white/55 mt-1.5 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-perf-excellent animate-pulse" />
        {t("livematch.realtime")} · {p.minute ? `${p.minute}′` : t("livematch.before_match")}
      </p>
    </div>
  );
}

export default function LiveMatch() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFollowed, toggle } = useFollows();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  const followed = isFollowed("foot", { match_id: String(id) });

  const load = (signal) => api.get(`/live/matches/${id}`, { signal }).then((r) => setM(r.data))
    .catch(() => { if (!signal?.aborted) setErr(true); });
  usePolling(load, 60000, [id]); // refresh every 60s

  const toggleFollow = () =>
    toggle("foot", { match_id: String(id), home: m?.home?.name, away: m?.away?.name, competition: m?.competition });

  if (err)
    return (
      <div className="min-h-screen grain flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-white/60 text-center">{t("livematch.not_live")}</p>
        <button onClick={() => navigate(-1)} className="px-4 py-2 rounded-xl bg-gold text-black font-bold">{t("livematch.back")}</button>
      </div>
    );
  if (!m) return <div className="min-h-screen grain flex items-center justify-center"><div className="w-32 h-32 rounded-full skeleton" /></div>;

  const hasScore = m.score && m.score.home != null;

  return (
    <div className="grain relative min-h-screen">
      <div className="relative z-10 max-w-2xl mx-auto px-5 pt-5">
        <div className="flex items-center justify-between">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-9 h-9 rounded-full glass flex items-center justify-center">
            <CaretLeft size={18} weight="bold" />
          </button>
          <div className="flex items-center gap-2">
            <LanguageSelector variant="header" />
            <button
              data-testid="follow-btn"
              onClick={toggleFollow}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                followed
                  ? "bg-perf-excellent/15 border border-perf-excellent/40 text-perf-excellent"
                  : "glass text-white/70 hover:text-white"
              }`}
            >
              {followed ? <BellRinging size={15} weight="fill" /> : <Bell size={15} weight="bold" />}
              {followed ? t("livematch.following") : t("livematch.follow")}
            </button>
          </div>
        </div>

        <p className="text-center text-[10px] font-bold uppercase tracking-[0.25em] text-gold mt-4">{m.competition}</p>
        <div className="flex items-center justify-center gap-5 mt-4">
          <div className="flex flex-col items-center gap-2 flex-1">
            <TeamBadge team={m.home} size={60} />
            <span className="text-sm font-bold text-center leading-tight">{m.home.name}</span>
          </div>
          <div className="text-center min-w-[90px]">
            {hasScore ? (
              <p className="font-numeral font-black text-4xl" style={{ color: m.is_live ? "#00E676" : "#fff" }}>
                {m.score.home} - {m.score.away}
              </p>
            ) : (
              <p className="font-numeral font-black text-3xl text-white/40">VS</p>
            )}
          </div>
          <div className="flex flex-col items-center gap-2 flex-1">
            <TeamBadge team={m.away} size={60} />
            <span className="text-sm font-bold text-center leading-tight">{m.away.name}</span>
          </div>
        </div>
        <div className="flex justify-center mt-4"><StatusPill m={m} /></div>
        {followed && (
          <p className="text-center text-[11px] text-perf-excellent/80 mt-2" data-testid="follow-hint">
            {t("livematch.follow_alert")}
          </p>
        )}

        <LivePreds fixtureId={id} />

        {m.xg && (
          <div className="hm-card p-4 mt-4" data-testid="xg-block">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={16} weight="fill" color="#00E676" />
              <h3 className="text-sm font-bold">Buts attendus (xG réel)</h3>
            </div>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-semibold truncate max-w-[38%]">{m.home.name}</span>
              <span className="text-white/40">xG</span>
              <span className="font-semibold truncate max-w-[38%] text-right">{m.away.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-numeral font-black text-perf-excellent w-10 text-left" data-testid="xg-home">{m.xg.home}</span>
              <div className="flex-1 flex h-2 rounded-full overflow-hidden bg-white/5">
                <div className="bg-perf-excellent" style={{ width: `${(m.xg.home / (m.xg.home + m.xg.away || 1)) * 100}%` }} />
                <div className="bg-gold" style={{ width: `${(m.xg.away / (m.xg.home + m.xg.away || 1)) * 100}%` }} />
              </div>
              <span className="font-numeral font-black text-gold w-10 text-right" data-testid="xg-away">{m.xg.away}</span>
            </div>
            <p className="text-[10px] text-white/40 mt-2">Source : statistiques officielles du match</p>
          </div>
        )}

        <div className="flex items-center gap-2 mt-8 mb-3">
          <Users size={18} weight="fill" color="#D4AF37" />
          <h3 className="font-display text-lg font-bold tracking-tight">{t("livematch.lineups")}</h3>
        </div>

        {m.lineups_available ? (
          <div className="grid gap-3">
            <Pitch side="home" data={m.lineups.home} accent="#00E676" />
            <Pitch side="away" data={m.lineups.away} accent="#D4AF37" />
          </div>
        ) : (
          <div className="hm-card p-6 text-center" data-testid="lineups-pending">
            <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-3">
              <Clock size={22} weight="bold" color="#D4AF37" />
            </div>
            <p className="text-sm font-semibold text-white/80">{t("livematch.lineups_soon")}</p>
            <p className="text-xs text-white/50 mt-1.5 max-w-xs mx-auto">
              {t("livematch.lineups_soon_desc")}
            </p>
          </div>
        )}
        <div className="h-8" />
      </div>
    </div>
  );
}
